import _ from 'lodash';
import { sqCapsuleRuntimeStore, sqDurationStore, sqTrendCapsuleStore, sqTrendStore } from '@/core/core.stores';
import { getCapsuleItems } from '@/trendData/trendCapsule.selectors';
import { derivePreviewChartItem } from '@/trendData/trendCapsule.utilities';
import {
  deriveChartItems,
  deriveStitchDetails,
  getChartCapsulesContext,
} from '@/trendData/trendCapsuleChart.utilities';
import {
  CHILD_CLONED_PROPERTIES,
  ITEM_CUSTOMIZATIONS,
  ITEM_TYPES,
  PREVIEW_ID,
  TREND_PANELS,
  TREND_VIEWS,
} from '@/trendData/trendData.constants';
import { warnToast } from '@/utilities/toast.utilities';
import type { Item } from '@/trendData/itemStore.utilities';
import type { PreviewChartItemPayload } from '@/trendData/trendCapsule.utilities';
import type { StitchBreak } from '@/trendData/trendCapsuleChart.utilities';
import type { CapsuleCalculatedChartItem, CapsuleItem, PreviewChartItem } from '@/trendData/trendData.types';

/**
 * Write-side helpers for the capsule runtime store. They orchestrate the derivations that follow a
 * dataset, selection, preview, or view change. They live outside the store because the derivations
 * pull in modules the store itself must not import.
 */

/**
 * Recomputes the runtime chart items from the runtime dataset, the flux-owned selection and
 * editing state, and the shared cross-store context.
 */
export function recomputeRuntimeChartItems(): void {
  const state = sqCapsuleRuntimeStore.getState();

  sqCapsuleRuntimeStore.actions.setChartItems(
    deriveChartItems(
      {
        calculatedChartItems: state.calculatedChartItems,
        previewChartItem: state.previewChartItem as CapsuleCalculatedChartItem,
        previewCapsulesDefinition: sqTrendCapsuleStore.previewCapsuleDefinition,
        editingId: sqTrendCapsuleStore.editingId,
        items: getCapsuleItems(),
        stitchBreaks: state.stitchBreaks,
        selectedCapsules: sqTrendCapsuleStore.selectedCapsules,
      },
      getChartCapsulesContext(),
    ),
  );
}

/**
 * Derives and applies the chain-view stitch details to the runtime store, warning when there are
 * more breaks than pixels and recomputing the chart when the details become set.
 */
export function writeRuntimeStitchDetails(numPixels: number): void {
  const { actions } = sqCapsuleRuntimeStore;

  // payload may be undefined when loading since we don't have the current pixels
  if (!numPixels || sqTrendStore.capsulePanelIsLoading) {
    actions.setStitchDetailsSet(false);
    return;
  }

  const items = getCapsuleItems();
  const selectedCapsules = _.filter(items, 'selected');
  const wasStitchDetailsSet = sqCapsuleRuntimeStore.getState().stitchDetailsSet;

  const details = deriveStitchDetails(selectedCapsules.length ? selectedCapsules : items, {
    numPixels,
    displayRangeStart: sqDurationStore.displayRange.start.valueOf(),
    displayRangeEnd: sqDurationStore.displayRange.end.valueOf(),
  });
  if (details.tooManyBreaks) {
    warnToast({ messageKey: 'TOO_MANY_BREAKS_CHAIN_VIEW' });
  }
  actions.applyStitchDetails(details);

  if (!wasStitchDetailsSet && sqCapsuleRuntimeStore.getState().stitchDetailsSet) {
    recomputeRuntimeChartItems();
  }
}

/** Records fetched chart capsules and re-derives the dependent state */
export function writeRuntimeChartCapsules(
  conditions: CapsuleCalculatedChartItem[],
  numPixels: number,
  add: boolean,
): void {
  const { actions } = sqCapsuleRuntimeStore;
  if (add) {
    actions.addCalculatedChartItems(_.flatten(conditions));
  } else {
    actions.setCalculatedChartItems(_.flatten(conditions));
  }
  if (sqTrendStore.view === TREND_VIEWS.CHAIN) {
    writeRuntimeStitchDetails(numPixels);
  }
  recomputeRuntimeChartItems();
}

/**
 * Records the preview chart item and re-derives the chart. The selection pruning lives with the
 * flux store, so this runs after the dispatch.
 */
export function writeRuntimePreviewChartItem(payload: PreviewChartItemPayload): void {
  sqCapsuleRuntimeStore.actions.setPreviewChartItem(derivePreviewChartItem(payload));
  recomputeRuntimeChartItems();
}

/** Clears the preview chart item and re-derives the chart if it was drawing preview rows */
export function clearRuntimePreviewChartItem(): void {
  const hadPreviewRows = sqCapsuleRuntimeStore
    .getState()
    .chartItems.some((chartItem) => chartItem.id.includes(PREVIEW_ID));
  sqCapsuleRuntimeStore.actions.setPreviewChartItem({});
  if (hadPreviewRows) {
    recomputeRuntimeChartItems();
  }
}

/** Shows an empty preview lane for the condition being edited */
export function writeRuntimeEmptyPreviewLane(): void {
  sqCapsuleRuntimeStore.actions.setPreviewChartItem({
    capsules: [],
    id: sqTrendCapsuleStore.editingId,
  });
  recomputeRuntimeChartItems();
}

/** Drops one condition's lane from a multi-lane preview */
export function removeRuntimeConditionFromPreviewLane(conditionId: string): void {
  const previewCapsules = _.clone(sqCapsuleRuntimeStore.getState().previewChartItem as CapsuleItem[]);
  _.remove(previewCapsules, (item) => item?.id === conditionId);
  sqCapsuleRuntimeStore.actions.setPreviewChartItem(previewCapsules as PreviewChartItem);
  recomputeRuntimeChartItems();
}

/**
 * Removes the capsules by exact id and refreshes the chain-view stitch details. The selection
 * pruning lives with the flux store.
 */
export function removeRuntimeCapsuleItems(payload: { items: Array<{ id: string }>; numPixels?: number }): void {
  sqCapsuleRuntimeStore.actions.removeItems(_.map(payload.items, 'id'));
  if (sqTrendStore.view === TREND_VIEWS.CHAIN) {
    writeRuntimeStitchDetails(payload.numPixels ?? 0);
  }
}

/**
 * Applies the chart effects of a selection change: a condition also syncs its calculated chart
 * items, and both capsules and conditions refresh the stitch details and the chart. Selection
 * itself lives with the flux store, so this runs after the dispatch.
 */
export function writeRuntimeSelectionEffects(payload: { item: Item; selected: boolean; numPixels?: number }): void {
  const itemType = payload.item.itemType;
  if (itemType !== ITEM_TYPES.CAPSULE && itemType !== ITEM_TYPES.CONDITION) {
    return;
  }
  if (itemType === ITEM_TYPES.CONDITION) {
    sqCapsuleRuntimeStore.actions.setCalculatedChartItemSelected(payload.item.id, payload.selected);
  }
  if (sqTrendStore.view === TREND_VIEWS.CHAIN) {
    writeRuntimeStitchDetails(payload.numPixels ?? 0);
  }
  recomputeRuntimeChartItems();
}

/**
 * Whether the id addresses the preview lane, either directly (PREVIEW_ID prefix) or as the
 * condition whose preview is being displayed.
 */
function isRuntimePreviewItem(id: string): boolean {
  const previewChartItem = sqCapsuleRuntimeStore.getState().previewChartItem as { id?: string };

  return !_.isEmpty(previewChartItem) && (_.startsWith(id, PREVIEW_ID) || _.endsWith(previewChartItem.id, id));
}

/**
 * Recolors everything the id addresses: the item itself, capsule children that clone color, the
 * chart rows of the condition, and the preview.
 */
export function writeRuntimeColor(id: string, color: string | undefined): void {
  const { actions } = sqCapsuleRuntimeStore;
  const state = sqCapsuleRuntimeStore.getState();

  if (isRuntimePreviewItem(id)) {
    actions.mergePreviewColor(color);
    if ((state.previewChartItem as { id?: string }).id === PREVIEW_ID) {
      recomputeRuntimeChartItems();
    }
  }

  const item = _.find(state.items, ['id', id]);
  if (item) {
    actions.setItemColors([id], color);
  }

  const children = _.filter(state.items, (candidate) => candidate.isChildOf === id);
  const cloneColorChildren = _.filter(children, (child) =>
    _.includes(CHILD_CLONED_PROPERTIES[child.childType as keyof typeof CHILD_CLONED_PROPERTIES], 'color'),
  );
  if (children.length > 0) {
    if (cloneColorChildren.length > 0) {
      actions.setItemColors(_.map(cloneColorChildren, 'id'), color);
      actions.setChartColorsForCondition(id, color);
    }
  } else if (state.chartItems.length > 0) {
    actions.setChartColorsForCondition(id, color);
  }
}

/**
 * Applies lane customizations to the preview lane when the customized item is the condition being
 * edited, so the preview keeps them across recomputes.
 */
export function writeRuntimeCustomizations(items: Array<{ id: string } & Record<string, unknown>>): void {
  items.forEach((item) => {
    if (isRuntimePreviewItem(item.id)) {
      sqCapsuleRuntimeStore.actions.mergePreviewChartItemProps(
        _.pick(item, [...ITEM_CUSTOMIZATIONS[ITEM_TYPES.CONDITION]]),
      );
    }
  });
}

/** Merges properties into a runtime capsule item, its children when asked, and the preview it addresses */
export function writeRuntimeProperties(
  payload: { id: string; includeChildren?: boolean } & Record<string, unknown>,
): void {
  const props = _.omit(payload, ['id', 'includeChildren']);
  if (isRuntimePreviewItem(payload.id)) {
    sqCapsuleRuntimeStore.actions.mergePreviewChartItemProps(props);
  }
  const ids = [payload.id];
  if (payload.includeChildren) {
    sqCapsuleRuntimeStore.getState().items.forEach((item) => {
      if (item.isChildOf === payload.id) {
        ids.push(item.id);
      }
    });
  }
  sqCapsuleRuntimeStore.actions.mergeItemProps(ids, props);
}

/** Recomputes the chart so uncertain capsule fills pick up the new dark-mode color */
export function refreshRuntimeUncertainItems(): void {
  const chartItems = sqCapsuleRuntimeStore.getState().chartItems;
  if (_.isEmpty(chartItems) || !_.some(chartItems, 'isUncertain')) {
    return;
  }
  recomputeRuntimeChartItems();
}

/** Sets the stitch breaks and recomputes the chart when capsule labels depend on them */
export function writeRuntimeStitchBreaks(stitchBreaks: StitchBreak[]): void {
  sqCapsuleRuntimeStore.actions.setStitchBreaks(stitchBreaks);
  if (!_.isEmpty(sqTrendStore.enabledColumns(TREND_PANELS.CHART_CAPSULES))) {
    recomputeRuntimeChartItems();
  }
}
